import React, { useState } from "react";
import { Box, Chip, CircularProgress, MenuItem, Select, Typography } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";

import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetBudgetRequestsQuery } from "state/rtkQuery/user/userQueries";

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const PER_PAGE = 5;

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  pending: { label: "Pending", color: "#F28C00", bg: "rgba(242,140,0,0.1)" },
  approved: { label: "Approved", color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  rejected: { label: "Rejected", color: USAGE_RED, bg: "rgba(226,35,26,0.1)" },
  failed: { label: "Failed", color: "#C53211", bg: "rgba(197,50,17,0.12)" },
  cancelled: { label: "Cancelled", color: "#666", bg: "rgba(0,0,0,0.06)" },
};

const formatUsd = (v) =>
  "$" + Number(v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Timestamps arrive as UTC with no zone designator, so treat them as UTC.
const formatDay = (iso) => {
  if (!iso) return "—";
  const date = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-US");
};

// Every AI-credit budget request this user has made in this org — the same
// card layout as "My Requests" in the Request AIC Budget dialog, read-only
// (an admin reviews requests from the table, not from here).
const UserRequestsTab = ({ email, orgId, platformId, open = true }) => {
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const { data, isFetching } = useGetBudgetRequestsQuery(
    { orgId, platformId, email, status, page, perPage: PER_PAGE, sortBy: "request_date", sortOrder: "desc" },
    { skip: !open || !email || !orgId || !platformId }
  );
  const requests = data?.requests ?? [];
  const nPages = Math.max(1, Math.ceil((data?.total ?? 0) / PER_PAGE));

  return (
    <Box sx={{ overflowY: "auto", flex: 1, p: 3, backgroundColor: "#fff" }}>
      <Box sx={{ mb: 1.5, minWidth: 160 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#333", mb: 0.5 }}>Status</Typography>
        <Select
          size="small"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          inputProps={{ "aria-label": "Request status" }}
          sx={{ backgroundColor: "#fff", fontSize: "0.82rem", minWidth: 160 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress size={22} sx={{ color: ACCENT }} />
        </Box>
      ) : !requests.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#bbb", fontStyle: "italic" }}>
            No budget requests found for this user.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            {requests.map((r) => {
              const isIncrease = r.action === "increase";
              const color = isIncrease ? ACCENT : USAGE_RED;
              const style = STATUS_STYLES[r.status] ?? STATUS_STYLES.pending;
              return (
                <Box
                  key={r.request_id}
                  sx={{
                    border: "1px solid #e8e8e8",
                    borderLeft: `3px solid ${style.color}`,
                    borderRadius: "10px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color }}>
                        {isIncrease ? <NorthEastIcon sx={{ fontSize: "1rem" }} /> : <SouthWestIcon sx={{ fontSize: "1rem" }} />}
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color }}>
                          {isIncrease ? "+" : "-"} {formatUsd(Math.abs(Number(r.change_amount ?? 0)))}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>{formatDay(r.request_date)}</Typography>
                    </Box>
                    {r.request_reason && (
                      <Typography sx={{ fontSize: "0.76rem", color: "#555", mt: 0.4 }}>{r.request_reason}</Typography>
                    )}
                    {(r.status === "approved" || r.status === "rejected") && (
                      <Typography sx={{ fontSize: "0.72rem", color: style.color, mt: 0.5 }}>
                        {style.label} by {r.reviewed_by ?? "—"} on {formatDay(r.reviewed_at)}
                        {r.action_reason ? ` — ${r.action_reason}` : ""}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={style.label} size="small" sx={{ color: style.color, backgroundColor: style.bg, fontWeight: 700, fontSize: "0.72rem" }} />
                </Box>
              );
            })}
          </Box>
          {nPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
              <Pagination nPages={nPages} currentPage={page} setCurrentPage={setPage} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UserRequestsTab;
